import { validateEmail } from '../../../utils/validation';

/** Extract a bare email from "Name <email@host>" or plain address tokens. */
export function extractRecipientEmail(token: string): string | null {
  const trimmed = token.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(.+?)\s*<([^>]+)>$/);
  const email = (match ? match[2] : trimmed).trim();
  return email.length > 0 ? email : null;
}

/** Parse comma/semicolon recipient field into bare emails for API send. */
export function parseRecipientFieldForSend(value: string | null | undefined): string[] | undefined {
  if (value == null || !String(value).trim()) return undefined;
  const emails = String(value)
    .split(/[,;]/)
    .map((part) => extractRecipientEmail(part))
    .filter((email): email is string => Boolean(email));
  return emails.length > 0 ? emails : undefined;
}

/** Split a recipient field into display tokens (badges), preserving original formatting. */
export function parseRecipientFieldTokens(value: string | null | undefined): string[] {
  if (value == null || !String(value).trim()) return [];
  return String(value)
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** True when token resolves to a syntactically valid email address. */
export function isValidRecipientToken(token: string): boolean {
  const email = extractRecipientEmail(token);
  if (!email) return false;
  return validateEmail(email).valid;
}

/** Tokens in a field that are not valid email addresses. */
export function getInvalidRecipientTokens(value: string | null | undefined): string[] {
  return parseRecipientFieldTokens(value).filter((token) => !isValidRecipientToken(token));
}

export function hasInvalidRecipients(fields: {
  to?: string;
  cc?: string;
  bcc?: string;
}): boolean {
  return (
    getInvalidRecipientTokens(fields.to).length > 0 ||
    getInvalidRecipientTokens(fields.cc).length > 0 ||
    getInvalidRecipientTokens(fields.bcc).length > 0
  );
}

/** Always send recipient strings on draft/template save (empty string clears stored values). */
export function recipientFieldsForPersistence(fields: {
  to?: string;
  cc?: string;
  bcc?: string;
}): { to: string; cc: string; bcc: string } {
  return {
    to: fields.to?.trim() ?? '',
    cc: fields.cc?.trim() ?? '',
    bcc: fields.bcc?.trim() ?? '',
  };
}
